// Basic / Advanced mode (#172). One app-wide switch: Basic hides options most
// people never need behind their defaults; Advanced shows everything. Hidden
// means defaulted, never disabled: switching back changes nothing but what
// shows.

import { useCallback, useContext, useState } from "react";
import useWorkoutSettings from "./useWorkoutSettings";
import AuthContext from "../contexts/AuthContext";
import * as api from "../services/api";

export default function useUiMode() {
  // Read without useAuth's provider check: the mode also gates sheets that
  // render outside the signed-in tree (and in tests).
  const isAuthenticated = !!useContext(AuthContext)?.isAuthenticated;
  const onSync = useCallback(
    (apiFields) => {
      if (!isAuthenticated) return;
      api.updateSettings({ ui_mode: apiFields.ui_mode }).catch(console.error);
    },
    [isAuthenticated],
  );
  const [settings, update] = useWorkoutSettings({ onSync });
  const advanced = settings.uiMode === "advanced";
  const setAdvanced = useCallback((on) => update({ uiMode: on ? "advanced" : "basic" }), [update]);
  return { advanced, setAdvanced };
}

/**
 * Whether a screen shows its Advanced options: always in Advanced, and in
 * Basic once "More options" has been tapped on this visit. Remounting the
 * screen tucks them away again.
 */
export function useShowAdvanced() {
  const { advanced } = useUiMode();
  const [revealed, setRevealed] = useState(false);
  return { show: advanced || revealed, advanced, reveal: () => setRevealed(true) };
}
